<template>
  <main>
    <b-container v-if="pool">
      <div class="page-breadcrumb">
        <router-link :to="{ name: 'PoolIndex' }"
          ><i class="fa fa-angle-left" /> Back to pools
        </router-link>
        <router-link v-if="$route.params.ref !== 'default' && poolLabel !== 'Process Complete'" :to="{ name: 'ProjectView', params: { id: $route.params.ref } }" class="ml-auto"
          >Go to project <i class="fa fa-angle-right" />
        </router-link>
        <router-link v-if="$route.params.ref !== 'default' && poolLabel === 'Process Complete'" :to="{ name: 'ModelCreate', params: { projectId: $route.params.ref } }" class="ml-auto"
          >Go back to model creation <i class="fa fa-angle-right" />
        </router-link>
      </div>
      
      <div class="section-header">
        <h1 class="heading">{{ pool.name }}</h1>

        <div class="actions">
          <div class="action" v-if="authenticatedUserName === pool.created_by">
            <b-btn
              variant="primary"
              @click="
                $router.push({
                  name: 'PoolEdit',
                  params: { id: pool._id }
                })
              "
            >
              <i class="fa fa-pencil-alt" />
              Edit pool
            </b-btn>
          </div>
        </div>
      </div>

      <div class="layoutcard">
        <table class="table table-hover vertical-align-middle">
          <tbody>
            <tr v-if="pool.pool_locked === 1">
              <td colspan="2">
                <b-alert variant="warning" show>Update restricted: Pool being used in one of active models</b-alert>
              </td>
            </tr>
            <tr>
              <th class="col-md-3">Source</th>
              <td>{{ pool.dbtype | ucFirst }}</td>
            </tr>
            <tr>
              <th>Status</th>
              <td>
                {{ pool.status | statusLabel }}

                <b-progress
                  :max="100"
                  class="mt-2"
                  show-progress
                  :animated="pool.status.code === 2"
                >
                  <b-progress-bar
                    :label="`${Math.round(pool.status.progress * 100)}% complete`"
                    :value="Math.round(pool.status.progress * 100)"
                    variant="success"
                  ></b-progress-bar>
                </b-progress>

                <span class="small">
                  {{ pool.stats.has_data }} / {{ pool.stats.size }}
                  <span class="text-muted">&ndash; {{ pool.status.label }}</span>
                </span>
                <span v-if="pool.stats.empty > 0" class="small text-danger ml-4">
                  {{ pool.stats.empty }} empty
                </span>
                <span v-if="pool.status.code === 3" class="small text-danger ml-4">
                  {{ pool.status.text }}
                </span>
                <b-alert v-if="docExceeded" variant="danger" show dismissible>Maximum document count exeeded set limit of 70k</b-alert>
                <b-alert v-if="processingError.code" variant="danger" show dismissible>{{ processingError.message }}</b-alert>
              </td>
            </tr>
            <tr>
              <th>Description</th>
              <td><code>{{pool.description}}</code></td>
            </tr>
            <tr>
              <th>Number of documents</th>
              <td>{{ pool.stats.size }}</td>
            </tr>
            <tr>
              <th>Created on</th>
              <td>{{ pool.creationDate | moment("DD-MM-YYYY H:mm:ss") }}</td>
            </tr>
            <tr>
              <th>Created by</th>
              <td>{{ pool.created_by }}</td>
            </tr>
            <tr>
              <th>Last modified</th>
              <td>{{ pool.lastModified | moment("DD-MM-YYYY H:mm:ss") }}</td>
            </tr>
            <tr v-if="pool.user_allowed.includes('Owner') && pool.dbtype !== 'core'">
              <th>Upload pool data</th>
              <td>
                <b-input-group>
                  <b-form-file v-model="file" :disabled="pool.pool_locked === 1"></b-form-file>
                  <b-input-group-append>
                    <b-btn :disabled="!file || uploading" @click="uploadFile" variant="outline-secondary">
                      <b-spinner v-if="uploading" small />
                      Add to Pool
                    </b-btn>
                  </b-input-group-append>
                </b-input-group>
                <b-radio-group v-model="filetype" class="mt-2">
                  <b-radio
                    v-for="(type, i) in filetypeOptions[pool.dbtype]"
                    :key="i"
                    :value="type"
                  >
                    {{ type.toUpperCase() }}
                  </b-radio>
                  <p class="text-info mt-1">File types allowed ({{filetypeOptions[pool.dbtype].toString()}}) | Max Size ({{ ["lens", "lens_patents"].includes(pool.dbtype)? "5GB":"1GB" }})</p>
                </b-radio-group>
              </td>
            </tr>
            <tr v-if="pool.dbtype === 'core' && !uploading && !processing && pool.status.code === 1 && pool.user_allowed.includes('Owner') && pool.pool_locked !== 1">
              <th>Query (Core format)</th>
              <td>
                <b-form-group
                    id="input-group-query"
                    label="(Max 500 Chars)"
                    label-for="input-query"
                    class="mb-4"
                  >
                  <b-form-textarea
                      id="input-query"
                      v-model.trim="form.query"
                      v-validate="{ required: true, max: 500 }"
                      placeholder='((Keyword) OR (Keyword)) AND ("Keyword" OR "Keyword") AND "Keyword"'
                      size="lg"
                      rows="8"
                      data-vv-name="query"
                  ></b-form-textarea>
                </b-form-group>
                <b-btn-group class="ml-auto">
                  <b-btn
                    variant="primary"
                    @click="addQueryToPool(pool._id)"
                  >
                    <i class="far fa-disk" /> Add Query to Pool
                  </b-btn>
                </b-btn-group>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="section-header">
        <h1 class="heading">{{ pool.dbtype==="core"? "Query added for pool":"Data files added to Pool" }}</h1>
        <p class="text-info text-right ml-auto mt-2">Maximum processing limit of documents per pool is less or equal to 70k</p>
        <div class="actions"></div>
      </div>

      <div class="layoutcard">
        <table v-if="pool.dbtype!=='core'" class="table table-hover vertical-align-middle">
          <tbody>
            <tr v-for="(doc, i) in pool.uploaddata" :key="i">
              <th>{{ doc._id.substring(doc._id.lastIndexOf("/") + 1) }}</th>
              <td>{{ doc.method }}</td>
              <td>{{ doc.type }}</td>
              <td>
                <b-btn-group>
                  <b-btn
                    :disabled="!pool.user_allowed.includes('Owner') || pool.pool_locked === 1"
                    variant="outline-secondary"
                    size="sm"
                    @click="deleteFileFromPool(doc._id)"
                  >
                    <i class="fa fa-trash" />
                  </b-btn>
                </b-btn-group>
              </td>
            </tr>
            <tr v-if="pool.uploaddata">
              <td v-if="pool.uploaddata.length === 0">
                No Documents added to the pool to process
              </td>
            </tr>
          </tbody>
        </table>
        <b-row v-else>
          <b-col class="d-flex">
            <code>{{pool.query}}</code>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col class="d-flex">
            <b-btn-group class="ml-auto">
              <b-btn
                variant="primary"
                :disabled="uploading || processing || pool.status.code > 1 || !pool.user_allowed.includes('Owner') || pool.pool_locked === 1"
                @click="startPoolProcess(pool._id)"
              >
                <i v-if="processing || poolLabel === 'Processing Pool'" class="far fa-hourglass" />
                <i v-else-if="poolLabel === 'Process Complete'" class="fa fa-check" />
                <i v-else-if="poolLabel === 'Restart'" class="fa fa-exclamation" />
                <i v-else class="fa fa-play"></i> {{ poolLabel }}
              </b-btn>
            </b-btn-group>
          </b-col>
        </b-row>
      </div>

      <!--
      <b-row class="mt-5">
        <b-col>
          <h2>Projects</h2>
        </b-col>
      </b-row>
      <table class="table custom-padding vertical-align-middle mt-3">
        <tr v-for="(project, i) in pool.projects" :key="i">
          <td><router-link :to="{ name: 'ProjectView', params: { id: project.id } }">{{ project.name }}</router-link></td>
        </tr>
      </table>
      -->
    </b-container>
  </main>
</template>

<script>
import axios from "axios";
import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import ApiRequest from "@/helpers/ApiRequest";
import InitAdmin from "../Shared/InitAdmin";

export default {
  name: "PoolView",
  extends: InitAdmin,
  data() {
    return {
      poolLabel: "Start Processing",
      interval: null,
      uploading: false,
      processing: false,
      docExceeded: false,
      processingError: {
        code: null,
        message: ""
      },
      pool: null,
      file: null,
      filetype: null,
      filetypeOptions: {
        derwent: ["txt"],
        plaintext: ["txt"],
        pubmed: ["txt"],
        reddit: ["api"],
        scopus: ["api", "txt"],
        derwentwos: ["txt", "xls", "xlsx"],
        bbso: ["xlsx"],
        cordis: ["xlsx"],
        kpmg: ["md"],
        google_patent: ["xlsx"],
        pro_quest: ["xls"],
        core: ["query"],
        lens: ["api"],
        lens_patents: ["api"],
        clinicaltrial: ["api"]
      },
      form: {
        query:null
      }
    };
  },
  computed: {
    ...mapGetters(["authenticatedUserId", "authenticatedUserName", "userById"]),
    ...mapState({
      users: state => state.user.users
    })
  },
  watch: {
    file(val) {
      this.validateFile(val);
    }
  },
  mounted() {
    this.fetchPool();
  },
  destroyed() {
    clearInterval(this.interval);
  },
  methods: {
    ...mapActions(["loadPool", "updatePool", "processPool", "deletePoolDoc"]),
    fetchPool() {
      this.loadPool({ id: this.$route.params.id }).then(pool => {
        this.pool = pool;
        if(!this.interval && this.pool.status.code === 2){
          this.interval = setInterval(this.fetchPool, 10000);
        }
        if (!this.filetype) {
          this.filetype = this.filetypeOptions[this.pool.dbtype][0];
        }
        if(this.pool.status.code === 1){
          this.poolLabel = "Process Pool";
        }
        if(this.pool.status.code === 2){
          this.poolLabel = "Processing Pool";
        }
        if(this.pool.status.code === 3){
          this.poolLabel = "Restart";
        }
        if(this.pool.status.code === 4){
          this.poolLabel = "Process Complete";
        }
      });
    },
    validateFile(file) {
      //console.log(this.filetype, file)
      /*if (
        ![
          "text/plain",
          "text/csv",
          "application/vnd.ms-excel",
          "application/json",
          "application/zip",
          "application/gzip"
        ].includes(file.type)
      ) {
        this.file = null;
        this.$toasted.show("Filetype not allowed!", { type: "error" });
      }*/
      if (["lens", "lens_patents"].includes(this.pool.dbtype) && file.size > 5368709120) {
        this.file = null;
        this.$toasted.show("File exceeding maximum size(5GB) limit", { type: "error" });
      }
      else if (!["lens", "lens_patents"].includes(this.pool.dbtype) && file.size > 1048576000) {
        this.file = null;
        this.$toasted.show("File exceeding maximum size(1GB) limit", { type: "error" });
      }
    },
    getFileType(){
      return this.filetype==="api"? "application/json":this.file.type;
    },
    uploadFile() {
      if (!this.file) {
        return;
      }

      const jwt = localStorage.getItem("jwt") || "";
      this.uploading = true;

      axios
        .get(
          Vue.prototype.$globalSettings.api_endpoint +
            "/upload?pool_id=" +
            this.$route.params.id +
            "&sourcetype=" +
            this.pool.dbtype +
            "&filetype=" +
            this.filetype +
            "&filename=" +
            this.file.name
              .split(".")
              .slice(0, -1)
              .join(".") +
            "&fileext=" + this.getFileType(),
          {
            headers: {
              Authorization: "Bearer " + jwt,
              "Requested-Data-Source": "project"
            }
          }
        )
        .then(res => {
          const url = res.data.url;
          const fields = res.data.fields;
          const formData = new FormData();
          formData.append("key", fields["key"]);
          formData.append("policy", fields["policy"]);
          formData.append("x-amz-algorithm", fields["x-amz-algorithm"]);
          formData.append("x-amz-credential", fields["x-amz-credential"]);
          formData.append("x-amz-date", fields["x-amz-date"]);
          formData.append("x-amz-meta-filetype", this.filetype);
          formData.append("x-amz-meta-pool", this.$route.params.id);
          formData.append("x-amz-meta-source", this.pool.dbtype);
          formData.append("x-amz-meta-user", this.authenticatedUserName);
          formData.append("x-amz-signature", fields["x-amz-signature"]);
          formData.append("file", this.file); // Make sure file is appended last to prevent errors

          ApiRequest.customPost(url, formData).then(res => {
            console.log(res);
            this.file = null;
            this.uploading = false;
            if (res !== undefined && res.status < 205) {
              this.addFileToPool(this.pool._id, fields["key"]);
              this.$toasted.show("File uploaded!", {
                type: "success"
              });
            } else {
              //this.$toasted.show("File upload Failure", { type: "error" });
            }
          });
        })
        .catch(response => {
          this.file = null;
          this.uploading = false;
          //this.$toasted.show(response, { type: "error" });
        });
    },
    addFileToPool(id, path) {
      let pool = this.pool;
      pool.uploaddata.push({ method: "upload", type: "txt", _id: path });
      this.updatePool({ id: id, pool: pool }).then(() => {
        this.fetchPool();
      });
    },
    addQueryToPool(id) {
      let pool = this.pool;
      pool.query = this.form.query;
      this.updatePool({ id: id, pool: pool }).then(() => {
        this.fetchPool();
      });
    },
    deleteFileFromPool(path) {
      this.deletePoolDoc({ id: this.pool._id, doc: path }).then(res => {
        if (res) {
          let pool = this.pool;
          let index = pool.uploaddata.find(x => x._id === path);
          pool.uploaddata.splice(index, 1);
          this.updatePool({ id: this.pool._id, pool: pool }).then(() => {
            this.fetchPool();
          });
        }
      });
    },
    startPoolProcess(id) {
      this.docExceeded = false;
      let docs = this.pool.uploaddata;
      if(docs.length > 0){
        this.processing = true;
        this.poolLabel = "Parsing Data";
        for (let i = 0; i < docs.length; i++) {
          let doc = docs[i]._id;
          this.startDocProcess(id, doc);
        }
      }
      else{
        this.startDocProcess(id, null);
      }
    },
    startDocProcess(id, doc) {
      this.processPool({ id: id, filename: doc }).then(res => {
        if (res.data.status === "Duplicates found") {
          this.$toasted.show(
            "File process Failure: " + res.data.status + " in " + doc,
            { type: "error" }
          );
          this.poolLabel = "Processing Pool";
          this.processing = false;
        }
        if (res.data.message === "Pool is too large") {
          this.docExceeded = true;
          this.poolLabel = "Process Pool";
          this.processing = false;
        }
        if (res.data.status === "ok") {
          this.fetchPool();
          this.poolLabel = "Process Complete";
          this.processing = false;
        }
        else{
          this.fetchPool();
          this.poolLabel = "Parsing Data";
          this.processing = true;
        }
      }).catch(res => {
        console.log(res);
        if (res.code) {
          this.processingError.code = res.data.code;
          this.processingError.message = res.data.status;
        }
      });

    }
  }
};
</script>
